// White-label prequalification — partner-slug + funnel-base helpers.
//
// Brand RESOLUTION lives in PrequalBrandContext: it is resolved once at the
// boundary of the flow and inherited by every surface, rather than re-derived
// per route. `usePartnerBrand` is re-exported from here so the surfaces' import
// sites are unchanged; it now takes NO argument, and the `overrideSlug`
// parameter it used to take is gone — a source that is not the address bar is
// given to the provider (`<PrequalBrandProvider source={…}>`), which is the one
// place that decides where a brand comes from.
//
// The two helpers below read the `:slug` ROUTE PARAM directly and deliberately
// do NOT go through the resolved brand. The reserved role words (buyer / seller
// / explore / result) are NEVER treated as a partner slug, so a native role
// route can never accidentally trigger a brand fetch.

import { useLocation, useParams } from "react-router-dom";
import { isPartnerSlug } from "./PrequalBrandContext";

export {
  PrequalBrandProvider,
  isPartnerSlug,
  usePartnerBrand,
  // Re-exported for the same reason usePartnerBrand is: the funnel surfaces take
  // their whole brand-and-copy vocabulary from one import site. Resolution lives
  // in the provider; a surface says what it renders, not where the words come from.
  useFunnelCopy,
  type PartnerBrandState,
} from "./PrequalBrandContext";

/**
 * The RAW partner slug from the route, or null on the native funnel.
 *
 * Deliberately not `usePartnerBrand().brand?.slug`: the draft calls must carry
 * the brand segment from the first render, and the brand resolves
 * asynchronously. The server resolves the slug itself and degrades an unknown
 * or inactive one to the native funnel — the same rule finalize has always
 * used — so sending the route value is both correct and available immediately.
 *
 * This also fingerprints the stored funnel state (PrequalSessionScope), where
 * the synchronous route value is the point: keying that guard on the resolved
 * brand would restore a previous applicant's answers first and check whose they
 * were second.
 */
export function usePrequalPartnerSlug(): string | null {
  const { slug } = useParams();
  return isPartnerSlug(slug) ? slug : null;
}

/** The funnel base path for the current route: `/prequalify` (native) or
 *  `/prequalify/{slug}` (partner) — and, under the legacy `/portal/prequalify`
 *  mount, the corresponding `/portal/prequalify[/{slug}]`. Every in-funnel
 *  link/redirect is built relative to this so the buyer/seller/explore/result
 *  paths stay within the same (native or partner) namespace. */
export function usePrequalBase(): string {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const marker = "/prequalify";
  const idx = pathname.indexOf(marker);
  const root = idx >= 0 ? pathname.slice(0, idx + marker.length) : marker;
  return isPartnerSlug(slug) ? `${root}/${slug}` : root;
}
